// Helpers for ContentType-specific attributes that aren't common across all Content.
// This file can hold helpers for any content type, not just conversations.

import type { Content, ContentConversationListItem, TransmittalPartySummary } from '@vivport/backend/types';

const MAX_VISIBLE_PARTIES = 2;

// A document ROOT row stands for its whole tree, so its tag indicator shows every tag
// carried anywhere in that tree — the backend supplies the union as descendantTagsIds.
export const resolveRowTagIds = (content: Content): number[] => {
  if (content.contentType === 'DOCUMENT' && content.descendantTagsIds) {
    return content.descendantTagsIds;
  }
  return content.tagsIds || [];
};

// Messages and events carry their own transmittal parties — senders, recipients,
// attendees — which is what the conversation rows read. `sharing` describes only who
// else inside the hub can see the content, so an imported email nobody has shared has
// an empty sharing summary while still having named participants.
const ownTransmittalParties = (content: Content): TransmittalPartySummary[] =>
  content.contentType === 'MESSAGE' || content.contentType === 'EVENT' ? content.parties : [];

export const isContentPrivate = (content: Content): boolean => {
  if (ownTransmittalParties(content).length > 0) return false;
  const sharing = content.sharing;
  if (!sharing) return true;
  return sharing.memberCount === 0 && (sharing.inheritedParties?.length ?? 0) === 0;
};

const normalizeText = (value: string | null | undefined): string => value?.trim().toLowerCase() ?? '';

const getPartyLabel = (
  party: TransmittalPartySummary,
  normalizedMeEmails: Set<string>,
  memberHasContributed: boolean
): { label: string; dedupeKey: string } | null => {
  const normalizedEmail = normalizeText(party.email);
  const normalizedName = normalizeText(party.name);

  if (normalizedEmail && normalizedMeEmails.has(normalizedEmail)) {
    if (!memberHasContributed) {
      return null;
    }
    return { label: 'me', dedupeKey: 'me' };
  }

  if (party.name?.trim()) {
    return { label: party.name.trim(), dedupeKey: normalizedEmail ? `email:${normalizedEmail}` : `name:${normalizedName}` };
  }

  if (party.email?.trim()) {
    return { label: party.email.trim(), dedupeKey: `email:${normalizedEmail}` };
  }

  return { label: 'unknown', dedupeKey: `party:${party.partyId}` };
};

export const buildPartySummary = (
  parties: TransmittalPartySummary[],
  meEmails: string[] = [],
  memberHasContributed: boolean = false
): string => {
  const normalizedMeEmails = new Set(meEmails.map(normalizeText).filter((email) => email.length > 0));
  const labels: string[] = [];
  const seen = new Set<string>();

  for (const party of parties) {
    const labelInfo = getPartyLabel(party, normalizedMeEmails, memberHasContributed);
    if (!labelInfo) {
      continue;
    }
    const { label, dedupeKey } = labelInfo;
    if (seen.has(dedupeKey)) {
      continue;
    }
    seen.add(dedupeKey);
    labels.push(label);
  }

  if (labels.length === 0) {
    return 'No parties';
  }

  if (labels.length <= MAX_VISIBLE_PARTIES) {
    return labels.join(', ');
  }

  const visible = labels.slice(0, MAX_VISIBLE_PARTIES);
  const overflow = labels.length - MAX_VISIBLE_PARTIES;
  return `${visible.join(', ')} +${overflow}`;
};

export const buildConversationPreview = (
  conversation: ContentConversationListItem
): { subject: string; snippet: string | null } => {
  const representative = conversation.representativeMessage;
  const subject =
    representative.subject?.trim() ||
    representative.title?.trim() ||
    `Conversation ${representative.code || representative.id}`;

  const rawSnippet =
    conversation.latestSnippet?.trim() ||
    representative.snippet?.trim() ||
    representative.description?.trim() ||
    null;

  if (!rawSnippet) {
    return { subject, snippet: null };
  }

  const normalizedSnippet = normalizeText(rawSnippet);
  const normalizedSubject = normalizeText(subject);
  return {
    subject,
    snippet: normalizedSnippet === normalizedSubject ? null : rawSnippet
  };
};

export const buildContentParticipantsLabel = (content: Content, meEmails: string[] = []): string => {
  // Same source, and so the same label, as the conversation rows in the Comms tab.
  const ownParties = ownTransmittalParties(content);
  if (ownParties.length > 0) {
    return buildPartySummary(ownParties, meEmails);
  }

  const creator = content.createdBy;
  const creatorFirst = (creator?.firstName ?? '').trim();
  const sharing = content.sharing;
  const inheritedParties = sharing?.inheritedParties ?? [];

  if (!sharing || sharing.memberCount === 0) {
    if (inheritedParties.length > 0) {
      return buildPartySummary(inheritedParties, meEmails);
    }
    return creatorFirst || 'unknown';
  }

  const names: string[] = [];
  const seen = new Set<string>();
  if (creatorFirst) {
    names.push(creatorFirst);
    seen.add(creatorFirst.toLowerCase());
  }
  for (const m of sharing.members) {
    const name = (m.firstName ?? '').trim();
    const key = name.toLowerCase();
    if (!name || seen.has(key)) continue;
    names.push(name);
    seen.add(key);
  }

  if (names.length === 0) return 'unknown';

  const total = (creatorFirst ? 1 : 0) + sharing.memberCount;
  if (total <= MAX_VISIBLE_PARTIES) {
    return names.slice(0, MAX_VISIBLE_PARTIES).join(', ');
  }
  return `${names.slice(0, MAX_VISIBLE_PARTIES).join(', ')} +${total - MAX_VISIBLE_PARTIES}`;
};

export const buildConversationMeta = (
  conversation: ContentConversationListItem,
  meEmails: string[] = []
): {
  partyNames: string;
  partyCountLabel: string;
  partySummary: string;
  subject: string;
  snippet: string | null;
  subtitle: string | null;
  latestTimelineAt: string;
} => {
  const partyNames = buildPartySummary(
    conversation.parties,
    meEmails,
    conversation.memberHasContributed
  );
  const partyCountLabel = `(${conversation.messageCount})`;
  const partySummary = `${partyNames} ${partyCountLabel}`;
  const preview = buildConversationPreview(conversation);
  const subtitle = preview.snippet ? `${partySummary} - ${preview.snippet}` : partySummary;
  return {
    partyNames,
    partyCountLabel,
    partySummary,
    subject: preview.subject,
    snippet: preview.snippet,
    subtitle,
    latestTimelineAt: conversation.latestMessageAt || conversation.representativeMessage.timelineAt
  };
};
